import { IDisplayItem, RightPanelType } from "@kuika/kuika-cl-model";
import { requestOpenEventInPanel } from "../../../right-panel/properties-panel/open-event-in-panel";
import { ISelectionOverlaySd } from "../selection-actions";

/** What every quick-prop control needs to read and act on the selected component. */
export interface IQuickContext {
  sd: ISelectionOverlaySd;
  kuikaId: string;
  displayItem: IDisplayItem;
}

/** Opens the right panel on Properties, where every property of the component can be edited. */
export function openPropertiesPanel(sd: ISelectionOverlaySd): void {
  sd.setActiveRightPanelByClick(RightPanelType.Properties);
}

/**
 * Brings one event's actions into view in the Properties panel (and, for `add`, opens its add-action
 * picker) — through `open-event-in-panel.ts`, which holds the request until the panel is mounted.
 */
export function openEventInPanel(ctx: IQuickContext, eventName: string, mode: "add" | "edit"): void {
  if (!ctx.sd.selectedKuikaIds.includes(ctx.kuikaId)) {
    ctx.sd.setSelectedDisplayItem(ctx.kuikaId);
  }
  openPropertiesPanel(ctx.sd);
  requestOpenEventInPanel({ kuikaId: ctx.kuikaId, eventName, mode });
}

/** `quickProp-<name>` style ids never contain spaces or dots that a selector would trip on. */
export const safeTestIdPart = (value: string): string => value.replace(/[^A-Za-z0-9_-]+/g, "-");

const warnedSkips: Set<string> = new Set<string>();

const describeEntry = (entry: unknown): string => {
  try {
    return JSON.stringify(entry) ?? String(entry);
  } catch {
    return String(entry);
  }
};

/**
 * A quick prop the bar cannot render is skipped, never thrown: component sets are data that can be newer
 * than the running designer (a new `kind`, a new style key) or hand-edited. In development it says so,
 * once per distinct entry — the bar re-renders often. The Component Set editor reports the same entries
 * through `validateQuickProps`.
 */
export function warnSkippedQuickProp(componentName: string | undefined, reason: string, entry: unknown): void {
  if (process.env.NODE_ENV !== "development") {
    return;
  }
  const description: string = describeEntry(entry);
  const key: string = `${componentName ?? ""}|${reason}|${description}`;
  if (warnedSkips.has(key)) {
    return;
  }
  warnedSkips.add(key);
  console.warn(`[quick props] Skipped a quick prop of "${componentName ?? "?"}" (${reason}): ${description}`);
}
